import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { transactionApiClient } from '../../api/transactionApiClient'
import { BankDto } from './Bank.types'
import { useEffect, useState } from 'react'
import { BehaviorSubject } from 'rxjs'
import { MOBILE_BREAKPOINT_DOWN } from '@monthio/design-system'

const isMobile$ = new BehaviorSubject(true)
const observer = new ResizeObserver((e) =>
	isMobile$.next(e[0]!.contentRect.width < MOBILE_BREAKPOINT_DOWN),
)
observer.observe(document.body)

const bankFetchOptions = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	revalidateOnReconnect: true,
	shouldRetryOnError: true,
}

const emptyArray: any[] = []
export const useBanks = (): { banks: BankDto[] } => {
	const { data } = useSWR(
		`/api/banks`,
		transactionApiClient.getBanks,
		bankFetchOptions,
	)

	return {
		banks: data ?? emptyArray,
	}
}

type MetaBankMap = Map<
	number,
	{
		id: number
		displayName: string
		banner: string
		icon: string
		favorite: boolean
	}
>

export const useBankMetaByBankId = (): MetaBankMap => {
	const { banks } = useBanks()
	const { t } = useTranslation()

	return banks.reduce((bankMap, bank) => {
		bankMap.set(bank.id, {
			id: bank.id,
			displayName:
				bank.displayName ??
				t('bankSessionOverview-moreAccounts-bankDisplayNamePending'),
			banner: bank.imageUrl,
			icon: bank.iconUrl,
			favorite: bank.favorite,
		})
		return bankMap
	}, new Map() as MetaBankMap)
}

const useBankFetcher = (id: string | number) => () => {
	return transactionApiClient.getBank(id)
}

export const useBank = (id: string | number) => {
	const { data, error } = useSWR(
		`/api/banks/${id}`,
		useBankFetcher(id),
		bankFetchOptions,
	)

	return {
		bank: data,
		isLoading: !error && !data,
		isError: error,
	}
}

export const useBankBanner = (bankId: number) => {
	const { banks } = useBanks()
	const { t } = useTranslation()
	const bank = banks.find(getBank(bankId))
	const banner = bank?.imageUrl ?? ''
	const alt =
		bank?.displayName ??
		t('bankSessionOverview-moreAccounts-bankDisplayNamePending')

	return {
		banner,
		alt,
	}
}

export const useBankIcon = (bankId: number) => {
	const { banks } = useBanks()
	const { t } = useTranslation()
	const bank = banks.find(getBank(bankId))
	const icon = bank?.iconUrl ?? ''
	const alt =
		bank?.displayName ??
		t('bankSessionOverview-moreAccounts-bankDisplayNamePending')

	return {
		icon,
		alt,
	}
}

export const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(true)
	useEffect(() => {
		isMobile$.subscribe(setIsMobile)
	}, [])
	return isMobile
}

const getBank = (bankId: number) => (b: { id: number }) => {
	return b.id === bankId
}
